// Polyfill Promise.withResolvers for older browsers (Safari < 17.4, Chrome < 119).
// Removable once browserslist target is raised. Without this, /tenants/reports
// throws TypeError on those browsers.
//
// OJO: esto SOLO cubre el hilo principal (`pdf.min.mjs` usa
// `Promise.withResolvers` 31 veces ahi, asi que sigue siendo necesario).
// El worker de pdf.js corre en otro realm y este polyfill NO lo alcanza:
// por eso este archivo llevaba meses con el polyfill y MB-APP-EXPRESS-X0
// seguia reportandose desde `pdf.worker.min.mjs`. El worker se cubre aparte,
// usando el build `legacy` que se auto-polyfillea dentro de su propio scope
// (ver `utils/configurePdfWorker.ts`). No borres ninguno de los dos creyendo
// que el otro basta.
if (typeof (Promise as { withResolvers?: unknown }).withResolvers !== "function") {
  (Promise as unknown as { withResolvers: <T>() => unknown }).withResolvers = function <T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

// Sentry must be imported first to capture all errors
import "./sentry";
import * as Sentry from "@sentry/react";

import React from "react";
import ReactDOM from "react-dom/client";
import { Provider as ReduxProvider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";

import "@ant-design/v5-patch-for-react-19";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ConfigProvider } from "antd";
import { Toaster } from "sonner";
import { store } from "store/store";

import App from "./App";
import "./i18n";
import "./main.css";
import "./styles/variables.scss";
import theme from "./theme";
import { configurePdfWorker } from "./utils/configurePdfWorker";
import {
  reportMutationError,
  reportQueryError,
} from "./utils/queryErrorReporting";

configurePdfWorker();

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    // Shared with the axios interceptor (see utils/queryErrorReporting.ts).
    onError: reportQueryError,
  }),
  // Sin esto, una mutacion que falla no deja rastro en ningun lado: las de
  // `api/creditReport.ts` usan `axios` pelado, asi que tampoco pasan por el
  // interceptor de AxiosInstance. Es el unico enganche global que react-query
  // ofrece para errores de mutacion, y va aqui porque aqui vive la
  // configuracion del QueryClient; la regla de que capturar y que ignorar es
  // la misma de las queries y esta compartida en
  // `utils/queryErrorReporting.ts`.
  mutationCache: new MutationCache({
    onError: reportMutationError,
  }),
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

const isDev = import.meta.env.VITE_APP_ENV === "development";
const RootComponent = isDev ? React.Fragment : React.StrictMode;

const container = document.getElementById("root") as HTMLElement;

// Create root with React 19 error hooks for Sentry
// Note: onCaughtError is intentionally omitted to let Sentry.ErrorBoundary handle caught errors
// This avoids duplicate captures and preserves React's dev mode error messages
const root = ReactDOM.createRoot(container, {
  // Capture errors that are thrown and NOT caught by an ErrorBoundary
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.error("Uncaught error:", error, errorInfo.componentStack);
  }),
  // Capture recoverable errors (e.g., during hydration)
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
  <RootComponent>
    <QueryClientProvider client={queryClient}>
      <ReduxProvider store={store}>
        <ConfigProvider theme={theme}>
          <Toaster
            position="top-center"
            richColors
            expand={false}
            duration={4000}
          />
          <Router>
            <App />
          </Router>
        </ConfigProvider>
      </ReduxProvider>
    </QueryClientProvider>
  </RootComponent>,
);
